'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'

import { Button, ContentBox, Dialog, Notice } from '@/shared/ui'
import { Form } from '@/shared/ui'

import { PassportImageForm } from './passport-dialog/passport-image-form'
import { PassportOCRLoading } from './passport-dialog/passport-ocr-loading'
import {
  createZPassportVerificationForm,
  passportVerificationFormDefaultValues,
  TPassportVerificationForm,
} from '../model/passport-verification-form'

type Props = {
  isScanning: boolean
  defaultValues?: Partial<TPassportVerificationForm>
  onClose: () => void
  submitText: string
  onSubmit: (formValues: TPassportVerificationForm) => void
  stepLabel?: string
}

export const PassportContent = ({
  isScanning,
  defaultValues,
  onClose,
  submitText,
  onSubmit,
  stepLabel,
}: Props) => {
  const t = useTranslations('passport_verification')
  const tCommon = useTranslations('common')

  const form = useForm<TPassportVerificationForm>({
    defaultValues: defaultValues || passportVerificationFormDefaultValues,
    resolver: zodResolver(createZPassportVerificationForm(t)),
    reValidateMode: 'onSubmit',
  })

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form

  return (
    <>
      <Dialog.Header className="p-6">
        <div className="flex items-center gap-2">
          <Dialog.Title>{t('title')}</Dialog.Title>
          {stepLabel && (
            <span className="typo-label2 font-light text-purple-500">
              {stepLabel}
            </span>
          )}
        </div>
        <Dialog.Description className="hidden">
          여권 사진을 등록해 주세요
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.Body className="flex grow flex-col px-6">
        {isScanning ? (
          <PassportOCRLoading />
        ) : (
          <>
            <p className="typo-label2 whitespace-break-spaces text-gray-500">
              {t('upload_description')}
            </p>
            <Form.Root form={form} className="mt-10 space-y-6">
              <Form.Field name="contactNumber">
                <Form.Label className="typo-label1">
                  {t('phone_number_label')}
                </Form.Label>
                <Form.TextField
                  type="tel"
                  placeholder={t('phone_number_placeholder')}
                  fullWidth
                />
                <Form.Message />
              </Form.Field>
              <PassportImageForm />
            </Form.Root>
            <ContentBox spacing="medium" color="light-gray" className="mt-3">
              <Notice.Title
                hasIcon={false}
                className="typo-label2 mb-2 font-medium text-purple-500"
              >
                {t('notice_title')}
              </Notice.Title>
              <Notice.List gap={false} className="text-black *:before:bg-black">
                <Notice.Item>
                  {t.rich('notice_item_take_front_photo', {
                    highlight: chunks => (
                      <strong className="font-bold">{chunks}</strong>
                    ),
                  })}
                </Notice.Item>
                <Notice.Item>{t('notice_item_no_glare')}</Notice.Item>
                <Notice.Item>
                  {t.rich('notice_item_passport_expiry', {
                    highlight: chunks => (
                      <strong className="font-bold">{chunks}</strong>
                    ),
                  })}
                </Notice.Item>
              </Notice.List>
            </ContentBox>
          </>
        )}
      </Dialog.Body>
      <Dialog.Footer className="flex-row gap-2.5 p-6">
        <Button
          color="secondary"
          size="large"
          fullWidth
          onClick={onClose}
          className="flex-1"
        >
          {tCommon('button.close')}
        </Button>
        <Button
          size="large"
          fullWidth
          className="flex-1"
          disabled={isScanning || isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          {submitText}
        </Button>
      </Dialog.Footer>
    </>
  )
}
